import Actions from 'actions'
import {
  GuidelineCategories,
  GuidelineItems,
  ActionTypes,
  PatientBriefActions,
  PatientBriefModalMode
} from 'consts/hiToolsConsts'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { isEmpty } from 'lodash'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'

export default ({ practiceGuidelines = {}, showRetentionObsSettings = false } = {}) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { copilotControl: copilotControlFF } = useFeatureFlags()

  const isCategoryEnabledForPatientBrief = useCallback(
    categoryKey => {
      if ([GuidelineCategories.RetentionTracking, GuidelineCategories.ObservationTracking].includes(categoryKey)) {
        return showRetentionObsSettings
      }

      return true
    },
    [showRetentionObsSettings]
  )

  const categoryValidators = useMemo(
    () => ({
      [GuidelineCategories.Elastics]: categoryValues =>
        isEmpty(categoryValues?.items?.[PatientBriefActions.Elastics]) &&
        t('dialogs.patientBrief.validationErrors.pleaseSetUpElastics'),
      [GuidelineCategories.TxGoals]: categoryValues =>
        !Object.values(categoryValues.items).some(txGoal => txGoal.isChecked) &&
        t('dialogs.patientBrief.validationErrors.noTxGoalsSelected'),
      [GuidelineCategories.IPR]: categoryValues =>
        !categoryValues.items[PatientBriefActions.AlignerNumberReached] &&
        t('dialogs.patientBrief.validationErrors.missingAlignerNo'),
      [GuidelineCategories.RPE]: categoryValues =>
        !categoryValues.items[PatientBriefActions.NumberOfTurns] &&
        t('dialogs.patientBrief.validationErrors.missingNumberOfTurns')
    }),
    [t]
  )

  const getDefaultItemsForCategory = useCallback(
    category => {
      switch (category) {
        case GuidelineCategories.AlignersTxTracking: {
          const categoryItems = practiceGuidelines.categories?.[GuidelineCategories.AlignersTxTracking]?.items
          return {
            typicalAdvanceFrequency: categoryItems?.[GuidelineItems.TypicalAdvanceFrequency]?.frequency
          }
        }
        case GuidelineCategories.BracesTxTracking: {
          const categoryItems = practiceGuidelines.categories?.[GuidelineCategories.BracesTxTracking]?.items
          return {
            wireSequence:
              categoryItems?.[GuidelineItems.ReadinessForWireChange]?.[ActionTypes.TypicalWireSequence]?.value
          }
        }
        case GuidelineCategories.RetentionTracking: {
          return {
            retainerType: '',
            wearTime: '',
            otherRetainerType: '',
            otherWearTime: ''
          }
        }
        case GuidelineCategories.ObservationTracking: {
          return {
            teethEruption: '',
            spacingCrowding: '',
            oralHealthImprovements: '',
            biteChange: '',
            other: ''
          }
        }

        default:
          return {}
      }
    },
    [practiceGuidelines]
  )

  const isCategoryAutoEnabledByStatusType = useCallback(
    (categoryKey, statusType) =>
      (categoryKey === GuidelineCategories.RetentionTracking && statusType === 'retention') ||
      (categoryKey === GuidelineCategories.ObservationTracking && statusType === 'observation'),
    []
  )

  const isCategoryVisibleForStatus = useCallback((categoryKey, statusType) => {
    if (categoryKey === GuidelineCategories.RetentionTracking) {
      return statusType === 'retention'
    }
    if (categoryKey === GuidelineCategories.ObservationTracking) {
      return statusType === 'observation'
    }
    return true
  }, [])

  const getDefaultCategories = useCallback(({ treatmentType, statusType }) => {
    let categories = [GuidelineCategories.OralHygiene]
    if (treatmentType === 'Aligners') {
      categories.push(GuidelineCategories.AlignersTxTracking)
    } else if (treatmentType === 'Braces') {
      categories.push(GuidelineCategories.BracesTxTracking)
    }

    if (statusType === 'retention') {
      categories.push(GuidelineCategories.RetentionTracking)
    }

    if (statusType === 'observation') {
      categories.push(GuidelineCategories.ObservationTracking)
    }

    return categories
  }, [])

  const filterDisabledCategories = useCallback(
    categories =>
      Object.keys(categories || {})
        .filter(categoryKey => isCategoryEnabledForPatientBrief(categoryKey))
        .reduce((final, categoryKey) => ({ ...final, [categoryKey]: categories[categoryKey] }), {}),
    [isCategoryEnabledForPatientBrief]
  )

  const buildDefaultPatientBrief = useCallback(
    ({ treatmentType, statusType }) => {
      if (!practiceGuidelines?.categories) {
        return {}
      }

      const defaultCategories = getDefaultCategories({ treatmentType, statusType })
      const categories = Object.keys(practiceGuidelines.categories)
        .filter(category => practiceGuidelines.categories[category].isEnabled)
        .filter(categoryKey => isCategoryEnabledForPatientBrief(categoryKey))
        .filter(categoryKey => isCategoryVisibleForStatus(categoryKey, statusType))
        .reduce(
          (patientBrief, category) => ({
            ...patientBrief,
            [category]: {
              isEnabled: defaultCategories.includes(category),
              note: '',
              items: getDefaultItemsForCategory(category)
            }
          }),
          {}
        )

      return {
        categories,
        customTitle: '',
        practiceNotes: ''
      }
    },
    [
      practiceGuidelines,
      getDefaultItemsForCategory,
      getDefaultCategories,
      isCategoryEnabledForPatientBrief,
      isCategoryVisibleForStatus
    ]
  )

  const createPatientBrief = useCallback(
    ({ patientId, customTitle, practiceNotes, categories, a_doctor, doctorId }) => {
      dispatch(
        Actions.createPatientBrief({
          patientId,
          customTitle,
          copilotControlFF,
          practiceNotes,
          categories,
          a_doctor,
          doctorId
        })
      )
    },
    [copilotControlFF, dispatch]
  )

  const validatePatientBrief = useCallback(
    values => {
      const categoryErrors = Object.keys(values.categories)
        .filter(category => values.categories[category].isEnabled && categoryValidators[category])
        .map(category => {
          const categoryValues = values.categories[category]
          const validator = categoryValidators[category]
          return { category, error: validator(categoryValues) }
        })
        .filter(({ error }) => !!error)
        .reduce(
          (final, { category, error }) => ({
            ...final,
            [category]: error
          }),
          {}
        )

      return {
        categories: categoryErrors
      }
    },
    [categoryValidators]
  )

  const loadPatientBriefValues = useCallback(
    ({ patientBrief, mode, statusType }) => {
      let categories = filterDisabledCategories(JSON.parse(patientBrief.categories || '{}'))
      if (mode === PatientBriefModalMode.View) {
        categories = Object.keys(categories)
          .filter(category => categories[category].isEnabled)
          .reduce((final, category) => ({ ...final, [category]: categories[category] }), {})
      } else {
        categories = Object.keys(categories)
          .filter(categoryKey => isCategoryVisibleForStatus(categoryKey, statusType))
          .reduce(
            (final, categoryKey) => ({
              ...final,
              [categoryKey]: {
                ...categories[categoryKey],
                isEnabled: isCategoryAutoEnabledByStatusType(categoryKey, statusType)
                  ? true
                  : categories[categoryKey].isEnabled
              }
            }),
            {}
          )

        const existingCategoryKeys = Object.keys(categories)
        const newCategories = Object.keys(practiceGuidelines.categories || {})
          .filter(
            categoryKey =>
              !existingCategoryKeys.includes(categoryKey) &&
              practiceGuidelines.categories[categoryKey]?.isEnabled &&
              isCategoryEnabledForPatientBrief(categoryKey) &&
              isCategoryVisibleForStatus(categoryKey, statusType)
          )
          .reduce(
            (final, category) => ({
              ...final,
              [category]: {
                ...practiceGuidelines.categories[category],
                isEnabled: isCategoryAutoEnabledByStatusType(category, statusType),
                note: '',
                items: getDefaultItemsForCategory(category)
              }
            }),
            {}
          )

        categories = {
          ...categories,
          ...newCategories
        }
      }

      return {
        categories,
        customTitle: mode === PatientBriefModalMode.Update ? '' : patientBrief.customTitle,
        practiceNotes: patientBrief.practiceNotes
      }
    },
    [
      filterDisabledCategories,
      getDefaultItemsForCategory,
      isCategoryAutoEnabledByStatusType,
      isCategoryEnabledForPatientBrief,
      isCategoryVisibleForStatus,
      practiceGuidelines
    ]
  )

  return {
    buildDefaultPatientBrief,
    createPatientBrief,
    validatePatientBrief,
    loadPatientBriefValues
  }
}
